/*
 * *****************************************************************************
 * File: auth.js (/src/pages/api/auth.js) | frontend
 * Written by Andrea Corsini <andrea@rs1.it>
 * =============================================================
 * Created on Tuesday, 23rd March 2021 2:19:03 pm
 *
 * Copyright (c) 2020-2021 Andrea Corsini T/A RS1 Project - All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * Modified on Wednesday, 31st March 2021 8:33:58 pm
 * *****************************************************************************
 */
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

let DEV_URL = ''
if (process.env.NODE_ENV === 'development') {
    DEV_URL = 'http://localhost:3000'
}

const Page = () => {
    const [state, setState] = useState({
        title: false,
        message: false,
        done: false,
        loading: true,
        error: false,
        mode: false,
        oobCode: false,
    })
    const [form, setForm] = useState({
        password: { value: '', error: false, initial: true },
        confirm: { value: '', error: false, initial: true },
        ready: false,
    })

    const location = useLocation()

    useEffect(() => {
        const { mode = false, oobCode = false } = queryString.parse(
            location.search
        )

        if (mode === 'verifyEmail' && oobCode) {
            setState(s => ({
                ...s,
                title: 'Verifica email',
                message: 'Verifica in corso',
                mode,
                oobCode,
            }))
            fetch(`${DEV_URL}/api/v1/auth`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ mode, oobCode }),
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error ${response.status}`)
                    }
                    return response.json()
                })
                .then(json => {
                    if (!json?.success)
                        throw new Error(
                            json?.message ||
                                'Impossibile comunicare con il server.'
                        )
                    setState(s => ({
                        ...s,
                        title: 'Perfetto!',
                        message: 'Email verificata con successo.',
                        loading: false,
                        error: false,
                        done: true,
                    }))
                })
                .catch(error => {
                    setState(s => ({
                        ...s,
                        message: error.message,
                        loading: false,
                        error: true,
                    }))
                })
        } else if (mode === 'resetPassword' && oobCode) {
            setState(s => ({
                ...s,
                title: 'Recupera password',
                message: 'Verifica in corso',
                mode,
                oobCode,
            }))
            fetch(`${DEV_URL}/api/v1/auth`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ mode, oobCode }),
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error ${response.status}`)
                    }
                    return response.json()
                })
                .then(json => {
                    if (!json?.success)
                        throw new Error(
                            json?.message ||
                                'Impossibile comunicare con il server.'
                        )
                    setState(s => ({
                        ...s,
                        message: json?.message || 'Inserisci la nuova password',
                        loading: false,
                        error: false,
                        done: false,
                    }))
                })
                .catch(error => {
                    setState(s => ({
                        ...s,
                        message: error.message,
                        loading: false,
                        error: true,
                    }))
                })
        } else {
            setState(s => ({
                ...s,
                title: 'Errore',
                message: 'Il link che hai seguito non è valido.',
                loading: false,
                error: true,
                done: false,
            }))
        }
    }, [location])

    useEffect(() => {
        setForm(f => ({
            ...f,
            ready:
                form.password.initial === false &&
                form.confirm.initial === false &&
                form.password.value === form.confirm.value &&
                form.password.error === false,
        }))
    }, [form.password, form.confirm])

    const savePassword = () => {
        if (!form.ready || !state.oobCode) return

        setState(s => ({
            ...s,
            message: 'Salvataggio in corso',
            loading: true,
            done: false,
            mode: 'updatePassword',
        }))
        fetch(`${DEV_URL}/api/v1/auth`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                mode: 'updatePassword',
                oobCode: state.oobCode,
                password: form.password.value,
            }),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`)
                }
                return response.json()
            })
            .then(json => {
                if (!json?.success)
                    throw new Error(
                        json?.message || 'Impossibile comunicare con il server.'
                    )
                setState(s => ({
                    ...s,
                    message:
                        json?.message || 'Password aggiornata con successo!',
                    loading: false,
                    error: false,
                    done: true,
                }))
            })
            .catch(error => {
                setState(s => ({
                    ...s,
                    message: error.message,
                    loading: false,
                    error: true,
                }))
            })
    }

    const isPasswordStrong = pwd =>
        pwd.length >= 8 &&
        /^.*[A-Z].*$/.test(pwd) &&
        /^.*[a-z].*$/.test(pwd) &&
        /^.*[0-9].*$/.test(pwd)

    return (
        <Container>
            {state.loading && !state.title ? (
                <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
                <>
                    {state.title && (
                        <Heading isDone={state.done} isError={state.error}>
                            {state.title}
                        </Heading>
                    )}
                    {state.message && <Content>{state.message}</Content>}
                    {state.loading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {!state.loading &&
                        !state.error &&
                        state.mode === 'resetPassword' && (
                            <Form>
                                <Label
                                    isError={form.password.error}
                                    isDone={
                                        !form.password.error &&
                                        !form.password.initial
                                    }
                                    htmlFor='newpassword'
                                >
                                    {form.password.error
                                        ? 'Password troppo debole!'
                                        : 'Nuova password:'}
                                    <input
                                        id='newpassword'
                                        type='password'
                                        value={form.password.value}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                password: {
                                                    value: e.target.value,
                                                    error: !isPasswordStrong(
                                                        e.target.value
                                                    ),
                                                    initial: false,
                                                },
                                            }))
                                        }
                                    />
                                </Label>
                                <Label
                                    isError={form.confirm.error}
                                    isDone={
                                        !form.confirm.error &&
                                        !form.confirm.initial
                                    }
                                    htmlFor='confirmpassword'
                                >
                                    {form.confirm.error
                                        ? 'Le password non corrispondono!'
                                        : 'Conferma password:'}
                                    <input
                                        id='confirmpassword'
                                        type='password'
                                        value={form.confirm.value}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                confirm: {
                                                    value: e.target.value,
                                                    error:
                                                        e.target.value !==
                                                        f.password.value,
                                                    initial: false,
                                                },
                                            }))
                                        }
                                    />
                                </Label>
                                <Guidelines>
                                    Assicurati che la nuova password soddisfi
                                    questi requisiti:
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;• almeno 8
                                    caratteri,
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;• almeno una lettera
                                    minuscola,
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;• almeno una lettera
                                    maiuscola,
                                    <br />
                                    &nbsp;&nbsp;&nbsp;&nbsp;• almeno un numero.
                                    <br />
                                </Guidelines>
                                {form.ready && (
                                    <Button onClick={savePassword}>
                                        <span>Salva nuova password</span>
                                    </Button>
                                )}
                            </Form>
                        )}
                    {state.done && (
                        <Button>
                            <a
                                href='https://apri.veicolo.app/'
                                target='_blank'
                                rel='noreferrer'
                            >
                                Torna a Veicolo
                            </a>
                        </Button>
                    )}
                </>
            )}
        </Container>
    )
}
export default Page

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 25px 0;
    font-size: 50px;
    color: #ffffff;
    z-index: 0;
`

const Heading = styled.h1`
    font-size: 34px;
    margin: 0 0 25px;
    color: ${props =>
        props.isError ? '#FF3B30' : props.isDone ? '#4BD964' : '#ffffff'};
`

const Content = styled.div`
    margin: 25px 0;
    font-size: 15px;
    line-height: 1.3;
`

const Button = styled.a`
    position: relative;
    display: block;
    font-weight: 600;
    border-radius: 8px;
    background: linear-gradient(
        135deg,
        rgba(67, 150, 199, 1) 0%,
        rgba(42, 94, 185, 1) 100%
    );
    padding: 2px;
    font-size: 14px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(255, 255, 255, 0.2);
    & a,
    span {
        cursor: pointer;
        display: block;
        padding: 8px 15px;
        border-radius: 7px;
        text-decoration: none;
        color: #ffffff;
        transition: all 0.1s linear;
        &:hover {
            background: #f2f1f6;
            color: #2a5eb9;
        }
    }
`

const Label = styled.label`
    color: ${props =>
        props.isError ? '#FF3B30' : props.isDone ? '#4BD964' : '#ffffff'};
    display: block;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    & input {
        background: #121212;
        display: block;
        width: 350px;
        border: 1px solid #333333;
        border-radius: 20px;
        height: 40px;
        line-height: 1;
        padding: 5px 10px;
        font-size: 15px;
        color: #ffffff;
        margin: 10px 0 25px;
        box-sizing: border-box;
        text-align: left;
    }
`

const Guidelines = styled.p`
    text-align: left;
    margin: 0 0 25px;
    font-style: italic;
    font-size: 15px;
    line-height: 1.5;
`

const Form = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`
